import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import Button from '@mui/material/Button';
import { useLocation } from 'react-router';
import Box from '@mui/material/Box';
import { slugs } from '../../hooks/slugs.js';
import { FONTS } from '../sections/theme.js';
import blogTranslations from '../../blog/translations.json' with { type: 'json' };
import { useTranslation } from 'react-i18next';
import { getBlogLang } from '../../utils/blogUtils.js';

const supportedLangs = [
    { name: 'language_tag.es', localName: 'Español', value: 'es' },
    { name: 'language_tag.en', localName: 'English', value: 'en' },
    { name: 'language_tag.pt', localName: 'Português', value: 'pt' },
];

function findBlogPath(toLanguage, path) {
    const blogLang = getBlogLang(path);
    if (blogLang === null) return null;

    const postMatch = path.match(/^\/blog(?:\/(es|pt))?\/posts\/(.+)$/);
    if (!postMatch) {
        return toLanguage === 'en' ? '/blog' : `/blog/${toLanguage}`;
    }

    const slug = postMatch[2];
    const group = blogTranslations.find((t) => t[blogLang] === slug);
    const altSlug = group?.[toLanguage];
    if (!altSlug) return toLanguage === 'en' ? '/blog' : `/blog/${toLanguage}`;
    return toLanguage === 'en' ? `/blog/posts/${altSlug}` : `/blog/${toLanguage}/posts/${altSlug}`;
}

function findSlugPath(currentLanguage, toLanguage, path) {
    const blogPath = findBlogPath(toLanguage, path);
    if (blogPath !== null) return blogPath;

    for (const [key, translations] of Object.entries(slugs)) {
        for (const translatedPath of Object.values(translations)) {
            if (`/${currentLanguage}/${translatedPath}` === path) {
                return `/${toLanguage}/${slugs[key][toLanguage]}`;
            }
        }
    }
    return `/${toLanguage}`;
}

function LangMenu() {
    const { t, i18n } = useTranslation();
    const [anchorElLangMenu, setAnchorElLangMenu] = useState(null);
    const location = useLocation().pathname;
    const language = i18n.language;

    const handleLangMenu = (event) => {
        setAnchorElLangMenu(event.currentTarget);
    };

    const handleLangMenuClose = () => {
        setAnchorElLangMenu(null);
    };

    if (!language) return null;

    return (
        <>
            <Button
                aria-controls="menu-lang"
                aria-haspopup="true"
                sx={{ p: 0 }}
                endIcon={<img alt={'language'} src={'/images/arrow_down.webp'} height={22} width={22} />}
                onClick={handleLangMenu}
            >
                <Typography
                    sx={{
                        ml: 2,
                        cursor: 'pointer',
                        textDecoration: 'none',
                        color: '#1E1E1E',
                        fontWeight: '700',
                        fontSize: 14,
                        lineHeight: '21px',
                        fontFamily: FONTS.sans,
                    }}
                >
                    {language.toLocaleUpperCase()}
                </Typography>
            </Button>
            <Menu
                id="menu-lang"
                anchorEl={anchorElLangMenu}
                keepMounted
                open={Boolean(anchorElLangMenu)}
                onClose={handleLangMenuClose}
                slotProps={{
                    paper: {
                        sx: {
                            width: { xs: '105px', md: '125px' },
                            left: '1200px',
                            borderRadius: '8px',
                        },
                    },
                }}
            >
                {supportedLangs.map((languageTag) => (
                    <MenuItem key={t(languageTag.name)} data-lang={languageTag.value}>
                        <Box
                            component={'a'}
                            href={findSlugPath(language, languageTag.value, location)}
                            sx={{
                                color: '#1E1E1E',
                                fontFamily: FONTS.sans,
                                fontSize: 14,
                                fontWeight: 500,
                                textDecoration: 'none',
                            }}
                        >
                            {languageTag.localName}
                        </Box>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

export default React.memo(LangMenu);
